import { SearchFilters } from "@outschool/gql-backend-generated";
import {
  SearchItem,
  usePlaceholderSuggestions,
} from "@outschool/ui-components-website";
import { useComponentTrackingContext } from "@outschool/ui-legacy-component-library";
import { useCombobox } from "downshift";
import { useState } from "react";
import { useDebouncedCallback } from "use-debounce/lib";

import { useSearchSuggestionsContext } from "../../../shared/client/providers/SearchSuggestionsProvider";
import {
  useSearchSuggestions,
  useSearchSuggestionsData,
} from "../../../shared/client/queries/ParentSearchSuggestions";
import { ExtendedSearchFiltersType } from "../../lib/useSearchFilters";

type EmptyFilterIgnoreFields =
  | "availableTimes"
  | "excludeNewOneOnOneFormat"
  | "excludeNonOngoingOneOnOne"
  | "excludeTimeWindows"
  | "includeInProgressFixedLengthLiveFormat"
  | "includeSelfPaced"
  | "isAvailable"
  | "order"
  | "q"
  | "seed"
  | "showListings"
  | "timeZone"
  | "topics"
  | "userName"
  | "userUid"
  | "learnerUid";

export const emptyFilter: Record<
  keyof Omit<SearchFilters, EmptyFilterIgnoreFields>,
  null
> = {
  activityRatingMin: null,
  adminTags: null,
  age: null,
  capacityMax: null,
  capacityMin: null,
  curriculums: null,
  delivery: null,
  dow: null,
  durationSessionMin: null,
  durationSessionMax: null,
  durationWeeksMax: null,
  durationWeeksMin: null,
  enabledBooleanFilters: null,
  endBy: null,
  endByTime: null,
  englishProficiencyLevel: null,
  format: null,
  gradeLevel: null,
  hasAssessment: null,
  hasGrades: null,
  hasHomework: null,
  hasFilledOutUniqueLearningNeeds: null,
  includedWithMembership: null,
  listUid: null,
  listUids: null,
  multiTermQuery: null,
  fundingPrograms: null,
  languageOfInstruction: null,
  learningNeeds: null,
  pricePerCourseMax: null,
  pricePerCourseMin: null,
  pricePerMeetingMin: null,
  pricePerMeetingMax: null,
  priceCreditsPerCourseMax: null,
  priceCreditsPerCourseMin: null,
  priceCreditsPerMeetingMin: null,
  priceCreditsPerMeetingMax: null,
  standards: null,
  starred: null,
  teacherRatingMin: null,
  time: null,
  timeOfDay: null,
  startAfter: null,
  startAfterTime: null,
  startBefore: null,
  theme: null,
  uniqueLearningNeeds: null,
  weeklyMeetingsMax: null,
  weeklyMeetingsMin: null,
};

export function queryFromSearchItem(
  searchItem: SearchItem,
  priorFilters: Pick<ExtendedSearchFiltersType, "order">,
): Pick<
  ExtendedSearchFiltersType,
  "userUid" | "userName" | "q" | "order" | "originalSpelling"
> {
  const isTeacher =
    searchItem.searchFilters?.userUid && searchItem.searchFilters?.userName;

  // clear all existing filters if it's saved or recent
  // otherwise, the old filters will stick around
  const searchFilters =
    searchItem.type === "saved" || searchItem.type === "recent"
      ? {
          ...emptyFilter,
          ...searchItem.searchFilters,
        }
      : searchItem.searchFilters;

  if (
    searchItem.type === "teacher" ||
    ((searchItem.type === "recent" || searchItem.type === "saved") && isTeacher)
  ) {
    return {
      ...searchFilters,
      q: undefined,
      userUid: searchItem.uid,
      userName: searchItem.name,
      originalSpelling: undefined,
      order: "upcoming",
    };
  } else {
    const res = {
      ...searchFilters,
      q: searchItem.value || searchItem.name,
      userUid: undefined,
      userName: undefined,
      originalSpelling: undefined,
      order: priorFilters.order === "upcoming" ? undefined : priorFilters.order,
    };

    return res;
  }
}

export default function useSearchWithSuggestions({
  onSearchFieldChange,
  onSearchFieldCommit,
  queryOptions = { ignore: [] },
  showSearchSuggestions = false,
}: {
  onSearchFieldChange: (item: SearchItem) => void;
  onSearchFieldCommit: (item: SearchItem) => void;
  queryOptions?: { ignore: Array<string> };
  showSearchSuggestions?: boolean;
}) {
  // Get initial item from context for initializing the combobox
  const searchSuggestionsContext = useSearchSuggestionsContext();
  const initialItem = searchSuggestionsContext?.initialSelectedItem;
  const initialInputValue = initialItem?.name ?? "";

  const [didUserFocusOnInput, setDidUserFocusOnInput] = useState(false);
  const [prevSearchQuery, setPrevSearchQuery] = useState(initialInputValue);
  // Track inputValue ourselves to compute suggestions before passing to combobox
  const [inputValue, setInputValue] = useState(initialInputValue);

  const track = useComponentTrackingContext();

  const {
    loading: loadingSuggestions,
    fetchSuggestions,
    popularTermSuggestions,
    teacherSuggestions,
    topicSuggestions,
    error,
  } = useSearchSuggestions(5, queryOptions.ignore);

  const placeholderSuggestions = usePlaceholderSuggestions();

  const [debouncedFetchSuggestions] = useDebouncedCallback((query: string) => {
    fetchSuggestions({ variables: { query, ignore: queryOptions.ignore } });
  }, 300);

  // Compute search suggestions as a memo - this comes before useCombobox
  // so we can pass the computed suggestions directly
  const searchSuggestions = useSearchSuggestionsData({
    inputValue,
    popularTermSuggestions,
    teacherSuggestions,
    topicSuggestions,
    placeholderSuggestions: showSearchSuggestions
      ? (placeholderSuggestions as SearchItem[])
      : [],
  });

  const {
    isOpen,
    getLabelProps,
    getMenuProps,
    getInputProps,
    getComboboxProps,
    highlightedIndex,
    getItemProps,
    openMenu,
    closeMenu,
    // Beware: Omitting this generic type caused TS4.9 to crash indecipherably
  } = useCombobox<SearchItem>({
    // Initialize with context values instead of using effects
    initialSelectedItem: initialItem ?? null,
    // Pass the computed suggestions directly
    items: searchSuggestions,
    // Use controlled inputValue pattern
    inputValue,
    itemToString: (item: SearchItem | null): string => {
      if (!item) {
        return "";
      }
      if (item.type === "keyword") {
        return inputValue || "";
      }
      return item?.value || item.name || "";
    },
    onSelectedItemChange: ({
      selectedItem,
      type,
      inputValue: newInputValue,
    }) => {
      if (type !== useCombobox.stateChangeTypes.FunctionSetInputValue) {
        closeMenu();
        if (!selectedItem) {
          return;
        }

        onSearchFieldCommit(selectedItem);
        if (inputValue && didUserFocusOnInput && selectedItem) {
          track(
            "listings_typeahead_touch",
            {
              inputValue: newInputValue,
              suggestionType: selectedItem.type,
              suggestionValue: selectedItem.value || selectedItem.name,
              suggestionUid: selectedItem.uid,
              positionInList: highlightedIndex,
              experiments: {
                mobileSearch: false,
              },
            },
            {
              // Redshift only options
              integrations: {
                All: false,
              },
            },
          );
        }
      }
    },
    onInputValueChange: ({ type, inputValue: newInputValue }) => {
      // Update our controlled inputValue state
      setInputValue(newInputValue ?? "");

      if (type === useCombobox.stateChangeTypes.InputChange) {
        onSearchFieldChange({
          type: "keyword",
          name: newInputValue,
          uid: newInputValue,
        });
      }
      if (newInputValue) {
        debouncedFetchSuggestions(newInputValue);
      }
    },
    stateReducer: (state, actionAndChanges) => {
      const { changes, type } = actionAndChanges;
      switch (type) {
        case useCombobox.stateChangeTypes.InputBlur:
          // Also update our controlled state when input blurs
          setInputValue(prevSearchQuery);
          return {
            ...changes,
            isOpen: false,
            inputValue: prevSearchQuery,
          };
        case useCombobox.stateChangeTypes.InputKeyDownEscape:
          return state;
        default:
          return changes;
      }
    },
  });

  const onInputFocus = () => {
    setDidUserFocusOnInput(true);
    openMenu();

    if (inputValue) {
      debouncedFetchSuggestions(inputValue);
    }
  };

  const onUserSubmit = (item: SearchItem) => {
    const itemInputValue = item.name || item.value;
    setPrevSearchQuery(itemInputValue || "");
  };

  return {
    isOpen,
    getLabelProps,
    getMenuProps,
    getInputProps,
    getComboboxProps,
    getItemProps,
    inputValue,
    setInputValue,
    onInputFocus,
    onUserSubmit,
    loadingSuggestions,
    suggestionsOpen: isOpen,
    searchSuggestions,
    suggestionsHighlightedIndex: highlightedIndex,
    error,
    closeMenu,
  };
}
